<template>
  <v-card flat class="px-6">
    <v-card-text>
      <v-form ref="form">
        <div class="d-flex align-center">
          <v-autocomplete
            v-model="selectedContractor"
            :items="filteredContractors"
            :loading="loading"
            :search-input.sync="search"
            item-text="displayName"
            item-value="id"
            return-object
            :label="$t('tslOrders.fields.selectClient')"
            :rules="[v => !!v || $t('tslOrders.messages.clientRequired')]"
            :no-data-text="noDataText"
            :placeholder="'Wpisz minimum 3 znaki...'"
            clearable
            outlined
            class="flex-grow-1"
            @change="onContractorChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.companyName }} ({{ item.city }})</v-list-item-title>
                <v-list-item-subtitle>{{ item.vatNumber }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-btn
            v-if="selectedContractor"
            icon
            color="primary"
            class="ml-2"
            @click="openContractorDetails"
          >
            <v-icon>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
        <v-btn
          text
          color="primary"
          class="mt-2"
          @click="showAddDialog = true"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t('tslOrders.addNewClient') }}
        </v-btn>

        <v-divider class="my-4" />

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="clientPhone"
              :label="$t('tslOrders.fields.clientPhone')"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="clientEmail"
              :label="$t('tslOrders.fields.clientEmail')"
              type="email"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="clientPrice"
              :label="$t('tslOrders.fields.clientPrice')"
              type="number"
              step="0.01"
              outlined
              :rules="[
                v => !!v || $t('tslOrders.messages.clientPriceRequired'),
                v => v > 0 || $t('tslOrders.messages.priceMustBeGreaterThanZero'),
                v => !v || /^\d+(\.\d{1,2})?$/.test(String(v)) || $t('tslOrders.messages.priceMaxTwoDecimalPlaces')
              ]"
              required
              @input="emitAdditionalFields"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="clientCurrency"
              :items="allCurrencies"
              item-text="description"
              item-value="id"
              :label="$t('tslOrders.fields.clientCurrency')"
              outlined
              :rules="[v => v !== null && v !== undefined || $t('tslOrders.messages.clientCurrencyRequired')]"
              required
              @change="emitAdditionalFields"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="clientRefNumber"
              :label="$t('tslOrders.fields.clientRefNumber')"
              outlined
              @input="emitAdditionalFields"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <AddTSLContractor
      v-model="showAddDialog"
      :dialog-mode="true"
      :contractor-type-override="1"
      @created="onContractorCreated"
      @close="showAddDialog = false"
    />
  </v-card>
</template>

<script>
import Service from '@/services'
import AddTSLContractor from '@/views/TSL/Contractors/AddTSLContractor'

export default {
  name: 'ClientTab',
  components: {
    AddTSLContractor
  },
  props: {
    value: {
      type: String,
      default: null
    },
    additionalFields: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      selectedContractor: null,
      contractors: [],
      loading: false,
      search: null,
      showAddDialog: false,
      clientPhone: this.additionalFields.clientPhone || '',
      clientEmail: this.additionalFields.clientEmail || '',
      clientPrice: this.additionalFields.clientPrice || null,
      clientCurrency: this.additionalFields.clientCurrency ?? 0,
      clientRefNumber: this.additionalFields.clientRefNumber || ''
    }
  },
  computed: {
    allCurrencies () {
      return this.$store.getters.allCurrencies
    },
    filteredContractors () {
      // Zawsze dodaj wybrany element, jeśli istnieje
      if (this.selectedContractor) {
        if (!this.search || this.search.length < 3) {
          return [this.selectedContractor]
        }
        // Jeśli wybrany element nie jest w wynikach wyszukiwania, dodaj go
        const hasSelected = this.contractors.some(c => c.id === this.selectedContractor.id)
        if (!hasSelected) {
          return [this.selectedContractor, ...this.contractors]
        }
      }
      if (!this.search || this.search.length < 3) {
        return []
      }
      return this.contractors
    },
    noDataText () {
      if (!this.search || this.search.length < 3) {
        return 'Wpisz minimum 3 znaki'
      }
      return 'Brak wyników'
    }
  },
  watch: {
    search (val) {
      if (val && val.length >= 3) {
        this.loadContractors()
      } else {
        this.contractors = []
      }
    },
    value: {
      immediate: true,
      handler (newVal) {
        if (newVal && !this.selectedContractor) {
          this.loadContractorById(newVal)
        }
      }
    },
    additionalFields: {
      deep: true,
      handler (newVal) {
        if (newVal) {
          this.clientPhone = newVal.clientPhone || ''
          this.clientEmail = newVal.clientEmail || ''
          this.clientPrice = newVal.clientPrice || null
          this.clientCurrency = newVal.clientCurrency ?? 0
          this.clientRefNumber = newVal.clientRefNumber || ''
        }
      }
    }
  },
  mounted () {
    // Load contractors only when searching (min 3 characters)
  },
  methods: {
    async loadContractors () {
      this.loading = true
      try {
        const { data } = await Service.get('/TSLContractor/GetContractors', {
          type: 1, // Client type
          page: 1,
          pageSize: 100,
          search: this.search
        })
        this.contractors = data.results.map(c => ({
          id: c.id,
          companyName: c.companyName,
          vatNumber: c.vatNumber,
          city: c.city,
          displayName: `${c.companyName} (${c.vatNumber})`
        }))
      } catch (e) {
        this.$store.dispatch('setErrorNotification', e.response?.data || 'Error loading clients')
      } finally {
        this.loading = false
      }
    },
    async loadContractorById (id) {
      try {
        const { data } = await Service.get('/TSLContractor/GetContractorDetails', { id })
        this.selectedContractor = {
          id: data.id,
          companyName: data.companyName,
          vatNumber: data.vatNumber,
          street: data.street,
          zipCode: data.zipCode,
          city: data.city,
          country: data.country,
          displayName: `${data.companyName} (${data.vatNumber})`
        }
      } catch (e) {
        console.error('Error loading contractor:', e)
      }
    },
    async onContractorChange () {
      if (this.selectedContractor?.id) {
        // Load full details when contractor is selected
        await this.loadContractorById(this.selectedContractor.id)
      }
      this.$emit('update', this.selectedContractor)
      this.$emit('input', this.selectedContractor?.id || null)
    },
    openContractorDetails () {
      if (this.selectedContractor) {
        const route = this.$router.resolve({
          name: 'TSLContractorDetails',
          params: { id: this.selectedContractor.id }
        })
        window.open(route.href, '_blank')
      }
    },
    async onContractorCreated (contractorData) {
      // Load full details of the newly created contractor
      await this.loadContractorById(contractorData.id)
      this.$emit('update', this.selectedContractor)
      this.$emit('input', contractorData.id)
      this.showAddDialog = false
    },
    emitAdditionalFields () {
      this.$emit('update-additional', {
        clientPhone: this.clientPhone,
        clientEmail: this.clientEmail,
        clientPrice: this.clientPrice,
        clientCurrency: this.clientCurrency,
        clientRefNumber: this.clientRefNumber
      })
    }
  }
}
</script>
